/**
 * Typed API client — single fetch wrapper that all React Query hooks use.
 *
 * Features:
 *  * JSON request + response by default
 *  * Clerk bearer token auto-attached via a pluggable token getter
 *  * `x-dev-user` fallback in dev when Clerk is not configured
 *  * Structured `ApiError` with status + problem-details body
 *  * Path-building helper `api.url("/players", { page: 2 })`
 */

import { env } from "@/config/env";

type TokenGetter = () => Promise<string | null>;

let tokenGetter: TokenGetter = () => Promise.resolve(null);

/** Register a Clerk-backed token getter at app startup. */
export function setTokenGetter(fn: TokenGetter): void {
  tokenGetter = fn;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type QueryValue = string | number | boolean | null | undefined;

function appendQuery(path: string, params?: Record<string, QueryValue | QueryValue[]>): string {
  if (!params) return path;
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) {
      for (const item of v) {
        if (item !== undefined && item !== null) usp.append(k, String(item));
      }
    } else {
      usp.append(k, String(v));
    }
  }
  const qs = usp.toString();
  return qs ? `${path}?${qs}` : path;
}

function devUserHeader(): string | undefined {
  if (!env.devAuthEnabled) return undefined;
  // URL query param wins (?dev=karel@test.local), else localStorage.
  try {
    const url = new URL(window.location.href);
    const q = url.searchParams.get("dev");
    if (q) {
      localStorage.setItem("rentier.devUser", q);
      return q;
    }
    return localStorage.getItem("rentier.devUser") ?? "karel@test.local";
  } catch {
    return "karel@test.local";
  }
}

type RequestInitLite = Omit<RequestInit, "body" | "headers"> & {
  headers?: Record<string, string>;
  json?: unknown;
};

async function request<T>(method: string, path: string, opts: RequestInitLite = {}): Promise<T> {
  const url = path.startsWith("http") ? path : `${env.apiV1}${path}`;

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...opts.headers,
  };

  if (opts.json !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  // Auth header — Clerk first, dev header fallback.
  const token = await tokenGetter();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  } else {
    const devUser = devUserHeader();
    if (devUser) {
      headers["x-dev-user"] = devUser;
    }
  }

  const init: RequestInit = {
    method,
    headers,
    credentials: "omit",
    ...opts,
  };
  if (opts.json !== undefined) {
    init.body = JSON.stringify(opts.json);
  }
  const response = await fetch(url, init);

  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      /* non-json error body — leave body null */
    }
    const rawDetail = (body as { detail?: unknown; message?: string })?.detail;
    let message: string;
    if (typeof rawDetail === "string") {
      message = rawDetail;
    } else if (Array.isArray(rawDetail)) {
      // FastAPI 422 Pydantic validation: [{loc, msg, type, input}...]
      message = rawDetail
        .map((e: { loc?: unknown[]; msg?: string }) => {
          const field = Array.isArray(e.loc)
            ? e.loc.filter((p) => p !== "body").join(".")
            : "";
          const msg = e.msg ?? "invalid";
          return field ? `${field}: ${msg}` : msg;
        })
        .join("; ");
    } else {
      message =
        (body as { message?: string })?.message ?? `HTTP ${response.status}`;
    }
    throw new ApiError(response.status, body, message);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, params?: Record<string, QueryValue | QueryValue[]>): Promise<T> =>
    request<T>("GET", appendQuery(path, params)),

  post: <T>(path: string, json?: unknown): Promise<T> => request<T>("POST", path, { json }),

  put: <T>(path: string, json?: unknown): Promise<T> => request<T>("PUT", path, { json }),

  patch: <T>(path: string, json?: unknown): Promise<T> => request<T>("PATCH", path, { json }),

  delete: <T>(path: string): Promise<T> => request<T>("DELETE", path),
};
